import { PUBLIC_HOME, isNative } from "@/lib/native";
import SiteMenu from "@/components/SiteMenu";
import { useEffect, useRef, useState } from "react";
import { Link, Navigate, useNavigate, useSearchParams } from "react-router-dom";
import { Send, Loader2, Sparkles, Lock, ArrowLeft } from "lucide-react";
import { api } from "@/lib/apiClient";
import { useAuth } from "@/context/AuthContext";
import CelestialBackground from "@/components/CelestialBackground";
import { Bubble, streamReply } from "@/pages/Ask";

const SIGNS = ["Aries", "Taurus", "Gemini", "Cancer", "Leo", "Virgo", "Libra", "Scorpio", "Sagittarius", "Capricorn", "Aquarius", "Pisces"];
const SUGGESTIONS = ["What does today's sky mean for me?", "Will this be a good week for love?", "What should I focus on right now?"];

function guestId() {
  let id = localStorage.getItem("astra_guest_id");
  if (!id) {
    id = crypto.randomUUID();
    localStorage.setItem("astra_guest_id", id);
  }
  return id;
}

function SignPicker({ onPick }) {
  const W = !isNative;
  return (
    <div data-testid="guest-sign-picker" className="mt-4">
      <p className="text-base text-[#D6D0C5] mb-3">What's your star sign? It helps the stars answer you personally.</p>
      <div className={`grid grid-cols-3 gap-2 ${W ? "lg:grid-cols-6 lg:gap-3" : ""}`}>
        {SIGNS.map((s) => (
          <button key={s} data-testid={`guest-sign-${s.toLowerCase()}`} onClick={() => onPick(s)}
            className="bg-[#0F1523] border border-[#D4AF37]/20 hover:border-[#D4AF37]/60 rounded-xl py-2.5 text-sm text-[#E8DCC4] transition-colors">
            {s}
          </button>
        ))}
      </div>
      <button data-testid="guest-sign-skip" onClick={() => onPick("")} className="w-full mt-3 text-sm text-[#A7B0C0] hover:text-[#E8DCC4] py-2">
        Skip, just let me ask
      </button>
    </div>
  );
}

function NudgeCard({ onClose }) {
  return (
    <div data-testid="guest-nudge-card" className="bg-[#0F1523] border border-[#D4AF37]/40 rounded-2xl p-4">
      <p className="text-base text-[#E8DCC4] mb-3">
        Enjoying this? Create a free account and Astra Interactive will answer from your full birth chart, plus 3 more questions.
      </p>
      <div className="flex items-center gap-3">
        <Link to="/join?next=ask" data-testid="btn-guest-nudge-join"
          className="text-sm text-[#04060B] bg-[#D4AF37] hover:bg-[#e6c04e] px-5 py-2 rounded-full transition-colors">
          Create free account
        </Link>
        <button type="button" data-testid="btn-guest-nudge-close" onClick={onClose} className="text-sm text-[#A7B0C0]">Not now</button>
      </div>
    </div>
  );
}

function OutCard() {
  return (
    <div data-testid="guest-out-card" className="mt-4 bg-[#0F1523] border border-[#D4AF37]/35 rounded-2xl p-5">
      <p className="flex items-center gap-2 text-[11px] uppercase tracking-[0.25em] gold-text mb-1"><Lock size={12} /> Free questions used</p>
      <p className="text-base text-[#D6D0C5] mb-4">Create your free account to keep going. Astra Interactive will then answer from your full birth chart.</p>
      <Link to="/join?next=ask" data-testid="btn-guest-create-account"
        className="block text-center w-full bg-[#D4AF37] hover:bg-[#e6c04e] text-[#04060B] font-medium text-lg py-3 rounded-full transition-colors">
        Create your free account
      </Link>
      <Link to={PUBLIC_HOME} data-testid="btn-guest-maybe-later" className="block text-center w-full mt-3 text-sm text-[#A7B0C0] hover:text-starlight py-2">
        Maybe later, back to the horoscopes
      </Link>
    </div>
  );
}

export default function GuestAsk() {
  const { user } = useAuth();
  const [params] = useSearchParams();
  const [gid] = useState(guestId);
  const [sign, setSign] = useState(() => localStorage.getItem("astra_guest_sign"));
  const [messages, setMessages] = useState(null);
  const [remaining, setRemaining] = useState(null);
  const [input, setInput] = useState(params.get("q") || "");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const endRef = useRef(null);
  const [nudgeClosed, setNudgeClosed] = useState(() => sessionStorage.getItem("astra_nudge_closed") === "1");

  useEffect(() => {
    api.get("/guest/chat", { params: { guest_id: gid } })
      .then(({ data }) => { setMessages(data.messages); setRemaining(data.remaining); })
      .catch(() => { setMessages([]); setRemaining(3); });
  }, [gid]);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);

  if (user) return <Navigate to={`/ask${params.get("q") ? `?q=${encodeURIComponent(params.get("q"))}` : ""}`} replace />;

  const pickSign = (s) => { localStorage.setItem("astra_guest_sign", s); setSign(s); };
  const out = remaining !== null && remaining <= 0;

  const send = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || busy || out) return;
    setError("");
    setInput("");
    setBusy(true);
    setMessages((p) => [...p, { role: "user", content: msg }, { role: "assistant", content: "" }]);
    try {
      await streamReply(
        "/guest/chat/stream",
        { guest_id: gid, message: msg, sign: sign || null },
        (d) => setMessages((p) => [...p.slice(0, -1), { ...p[p.length - 1], content: p[p.length - 1].content + d }]),
        (f) => setMessages((p) => [...p.slice(0, -1), { ...p[p.length - 1], followups: f }]),
        () => {},
      );
      setRemaining((r) => r - 1);
    } catch (e) {
      setMessages((p) => p.slice(0, -2));
      if (e.status === 402) setRemaining(0);
      else { setInput(msg); setError(e.message); }
    }
    setBusy(false);
  };

  const last = messages?.[messages.length - 1];
  const answers = messages?.filter((m) => m.role === "assistant" && m.content).length || 0;
  const showNudge = !busy && !out && !nudgeClosed && answers >= 2;
  const closeNudge = () => { sessionStorage.setItem("astra_nudge_closed", "1"); setNudgeClosed(true); };

  const W = !isNative;
  const composer = (inline) => (
        <form onSubmit={(e) => { e.preventDefault(); send(); }}
          data-testid={inline ? "guest-composer-inline" : "guest-composer"}
          className={inline ? `mt-4 flex gap-2 ${W ? "lg:col-span-3 lg:mt-2" : ""}` : `fixed bottom-0 left-0 right-0 max-w-md mx-auto px-4 pt-3 safe-bottom bg-[#04060B]/90 backdrop-blur-xl border-t border-[#D4AF37]/15 flex gap-2 z-30 ${W ? "lg:max-w-3xl lg:bottom-6 lg:pb-3 lg:px-3 lg:border lg:rounded-full" : ""}`}>
          <input data-testid="guest-chat-input" value={input} onChange={(e) => setInput(e.target.value)} maxLength={1000} enterKeyHint="send"
            placeholder="Ask the stars…"
            className="flex-1 bg-[#0F1523] border border-[#D4AF37]/25 rounded-full px-4 py-3 text-base text-starlight placeholder:text-[#5f6b7d] focus:outline-none focus:border-[#D4AF37]/60" />
          <button data-testid="guest-chat-send" type="submit" disabled={busy || !input.trim()}
            className="w-12 h-12 shrink-0 flex items-center justify-center rounded-full bg-[#D4AF37] hover:bg-[#e6c04e] text-[#04060B] disabled:opacity-50 transition-colors">
            {busy ? <Loader2 size={18} className="animate-spin" /> : <Send size={18} />}
          </button>
        </form>
  );

  return (
    <div className={`app-shell relative text-starlight ${W ? "wide-desktop" : ""}`}>
      <CelestialBackground />
      <div className={`relative z-10 px-5 pb-36 ${W ? "lg:max-w-3xl lg:mx-auto lg:px-8 lg:pb-40" : ""}`} data-testid="guest-ask-page">
        <header className={`safe-top box-content h-16 flex items-center gap-2 ${W ? "lg:h-20 lg:mb-6" : ""}`}>
          <SiteMenu />
          <BackButton />
          <Link to={PUBLIC_HOME} className="flex items-center gap-2" data-testid="link-guest-home">
            <img src="/astra-mark-128.png" alt="" aria-hidden="true" className="w-9 h-9" />
            <span className="font-display text-lg tracking-[0.3em]">ASTRA</span>
          </Link>
          {W && (
            <nav className="hidden lg:flex items-center gap-8 ml-12 text-base text-[#D6D0C5]">
              <Link to="/" data-testid="guest-nav-horoscopes" className="hover:text-starlight transition-colors">Horoscopes</Link>
              <Link to="/readings" data-testid="guest-nav-readings" className="hover:text-starlight transition-colors">Readings</Link>
            </nav>
          )}
          <Link to="/join?mode=login" className="ml-auto text-sm gold-text py-2.5 px-1" data-testid="link-guest-signin">Sign in</Link>
        </header>
        <p className="text-[11px] uppercase tracking-[0.3em] gold-text">Ask the stars · No sign-up</p>
        <h1 className={`text-3xl font-light mt-1 ${W ? "lg:text-5xl lg:mb-2" : ""}`}>Astra Interactive</h1>
        {remaining !== null && (
          <p data-testid="guest-quota" className="text-sm text-[#A7B0C0] mt-1">
            {out ? "You've used your 3 free questions." : `${remaining} of 3 free questions left`}{sign ? ` · ${sign}` : ""}
          </p>
        )}

        {messages === null ? (
          <Loader2 className="animate-spin gold-text mx-auto my-20" size={26} />
        ) : sign === null && messages.length === 0 ? (
          <SignPicker onPick={pickSign} />
        ) : messages.length === 0 && !out ? (
          <div className={`mt-5 space-y-2 ${W ? "lg:mt-8 lg:grid lg:grid-cols-3 lg:gap-3 lg:space-y-0" : ""}`} data-testid="guest-empty">
            {SUGGESTIONS.map((s, i) => (
              <button key={s} data-testid={`guest-suggestion-${i}`} onClick={() => send(s)}
                className={`w-full text-left flex items-center gap-2 bg-[#0F1523] border border-[#D4AF37]/20 hover:border-[#D4AF37]/50 rounded-2xl px-4 py-3 text-sm text-[#E8DCC4] transition-colors ${W ? "lg:items-start lg:py-5 lg:text-base" : ""}`}>
                <Sparkles size={14} className="gold-text shrink-0" /> {s}
              </button>
            ))}
            {composer(true)}
          </div>
        ) : (
          <div className="space-y-3 mt-4" data-testid="guest-messages">
            {messages.map((m, i) => <Bubble key={i} m={m} i={i} />)}
            {showNudge && <NudgeCard onClose={closeNudge} />}
            {!busy && !out && last?.followups?.length > 0 && (
              <div className="flex flex-col items-start gap-2 pt-1" data-testid="guest-followups">
                {last.followups.map((f, i) => (
                  <button key={f} data-testid={`guest-followup-${i}`} onClick={() => send(f)}
                    className="text-left flex items-center gap-2 border border-[#D4AF37]/35 hover:bg-[#D4AF37]/10 rounded-full px-4 py-2 text-sm text-[#E8DCC4] transition-colors">
                    <Sparkles size={13} className="gold-text shrink-0" /> {f}
                  </button>
                ))}
              </div>
            )}
            <div ref={endRef} />
          </div>
        )}
        {out && <OutCard />}
        {error && <p data-testid="guest-error" className="text-sm text-red-300 mt-3">{error}</p>}
      </div>

      {!out && sign !== null && messages?.length > 0 && composer(false)}
    </div>
  );
}

function BackButton() {
  const navigate = useNavigate();
  const back = () => (window.history.state?.idx > 0 ? navigate(-1) : navigate(PUBLIC_HOME));
  return (
    <button onClick={back} aria-label="Back" data-testid="btn-guest-back" className="-ml-1 p-1.5 text-[#A7B0C0] hover:text-starlight">
      <ArrowLeft size={20} />
    </button>
  );
}
